/**
 * The snapshot's entity `kind` byte (PROTOCOL.md 5.2): what an entity is, so the client draws it and the
 * map server knows which verbs it offers (ADR-051). Kinds 2 to 4 and 6 to 9 are things: object types (ADR-042) whose
 * state index rides the record's animation bits; kind 5 is a creature (ADR-074 decision 10); kind 6 is the training
 * dummy (ADR-058's amendment for P2.2-T7); kind 7 is the chest (ADR-091 decision 17); kind 8 is the shrine (ADR-096
 * decision 1), whose state never changes on the wire; kind 9 is the lantern (ADR-058 decision 7); kind 10 is a ground
 * item (ADR-091's amendment for P2.8-T13, decision 3), its appearance the item's number and its animation bits owned or
 * public; kind 11 is an NPC (ADR-136 decision 1), a folk row of the creature store that carries a name from the look table
 * and Health and Level only when its temperament fights. A waypoint (ADR-137 decision 2) travels as kind 8, a shrine's,
 * since the page rests, pops and sounds it as a shrine; `objectTypeOfKind(8)` names the shrine type. Values are wire
 * constants; new kinds append.
 */
export const EntityKind = { Player: 1, Sign: 2, Well: 3, Gate: 4, Creature: 5, Dummy: 6, Chest: 7, Shrine: 8, Lantern: 9, GroundItem: 10, Npc: 11 } as const;
export type EntityKind = (typeof EntityKind)[keyof typeof EntityKind];

const KIND_BY_TYPE: ReadonlyMap<string, EntityKind> = new Map([
  ["sign", EntityKind.Sign],
  ["well", EntityKind.Well],
  ["gate", EntityKind.Gate],
  ["dummy", EntityKind.Dummy],
  ["chest", EntityKind.Chest],
  ["shrine", EntityKind.Shrine],
  ["lantern", EntityKind.Lantern],
]);

/**
 * Object types that travel under another type's kind, so the kind names that other type.
 */
const KIND_ALIASES: ReadonlyMap<string, EntityKind> = new Map([["waypoint", EntityKind.Shrine]]);

const TYPE_BY_KIND: ReadonlyMap<number, string> = new Map([...KIND_BY_TYPE].map(([type, kind]) => [kind, type]));

/**
 * The kind an object type's instances carry.
 *
 * @param type The object type id.
 * @returns The kind, or undefined for a type with no kind.
 */
export function objectKindOf(type: string): EntityKind | undefined {
  return KIND_BY_TYPE.get(type) ?? KIND_ALIASES.get(type);
}

/**
 * The object type id of a thing kind.
 *
 * @param kind The kind byte.
 * @returns The type id, or undefined for the player or an unknown kind.
 */
export function objectTypeOfKind(kind: number): string | undefined {
  return TYPE_BY_KIND.get(kind);
}

/**
 * Whether a kind is a thing rather than a player.
 *
 * @param kind The kind byte.
 * @returns True for an object kind.
 */
export function isThingKind(kind: number): boolean {
  return TYPE_BY_KIND.has(kind);
}

/**
 * Whether a kind is a creature (ADR-074 decision 10): a third class beside the player and the things, whose
 * animation bits carry the character states and whose Health and Level are real.
 *
 * @param kind The kind byte.
 * @returns True for kind 5.
 */
export function isCreatureKind(kind: number): boolean {
  return kind === EntityKind.Creature;
}

/**
 * Whether a kind is an NPC (ADR-136 decision 1): a placed character a player can talk to, a fourth class beside the
 * player, the things and the creatures.
 *
 * @param kind The kind byte.
 * @returns True for kind 11.
 */
export function isNpcKind(kind: number): boolean {
  return kind === EntityKind.Npc;
}
